import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-limo-2.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryExec from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryLimo from "@/assets/sprinter-limo-3.jpg?format=webp&quality=72&w=1400&imagetools";
import gallerySuv from "@/assets/suburban-suv-game-day-atlanta.webp?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL =
  "https://atlantaluxurysprinters.com/chauffeur-service-buckhead-from-airport";
const PAGE_TITLE = "Chauffeur Service Buckhead From Airport | Atlanta Luxury Transfers";
const PAGE_DESC =
  "Luxury chauffeur service to Buckhead from any Atlanta airport, ATL, PDK, FTY, RYY, and LZU. Mercedes-Benz Sprinter and Suburban SUV transfers for executives and VIP travelers.";

const faqs = [
  {
    q: "Do you provide chauffeur service from PDK to Buckhead?",
    a: "Yes, PDK to Buckhead is one of our most-requested transfers. Travel time is typically 15 minutes, and our chauffeurs cover all four PDK FBOs (Atlantic Aviation, Million Air, Signature, Epps Aviation).",
  },
  {
    q: "Can you provide chauffeur service from ATL FBOs to Buckhead?",
    a: "Yes. We meet private aviation clients at Signature Flight Support and other Hartsfield-Jackson FBOs and transfer them directly to Buckhead hotels, residences, and offices.",
  },
  {
    q: "What vehicles are best for executive Buckhead arrivals?",
    a: "Suburban SUVs for principals and small parties; Mercedes-Benz Sprinter Limousines for groups of 8–14 with luggage. Both are chauffeur-driven and finished to a luxury standard.",
  },
  {
    q: "Do you handle multi-day Buckhead executive itineraries?",
    a: "Yes. We routinely manage as-directed and multi-day chauffeur programs in Buckhead, meetings, dinners, events, with the same vehicle and chauffeur for continuity.",
  },
  {
    q: "How long is the drive from Hartsfield-Jackson to Buckhead?",
    a: "About 17 miles and 30–45 minutes in normal conditions. Between 4pm and 7pm on weekdays it stretches to 50–65 minutes on the Downtown Connector and GA-400. Our chauffeurs route via I-285 and Roswell Rd when the Connector is stacked, the mileage is longer and the arrival is earlier.",
  },
  {
    q: "Is PDK faster into Buckhead than ATL?",
    a: "Substantially. PDK to Buckhead is roughly 11 miles and 20–30 minutes with no downtown exposure at all, which is why most private flyers with Buckhead business choose PDK over Hartsfield-Jackson.",
  },
  {
    q: "How does hotel arrival work at the St. Regis, Four Seasons, or Mandarin Oriental?",
    a: "We pre-notify the hotel's arrival desk with your name and ETA so the door team is expecting you. The chauffeur pulls into the porte-cochère, hands your bags to the bell team, and stays until you're checked in, no self-parking, no circling Peachtree Rd.",
  },
  {
    q: "Can you handle arrivals into a private Buckhead residence or gated community?",
    a: "Yes. Send the gate code or the name on the guest list when you book and we clear access in advance. Our chauffeurs work Tuxedo Park, Chastain Park, Peachtree Heights, and the Buckhead high-rises regularly and know which entrances a Sprinter actually fits through.",
  },
  {
    q: "Do you cover Buckhead corporate towers for executive arrivals?",
    a: "Daily. Tower Place, 3344 Peachtree, Terminus, Atlanta Financial Center, and Piedmont Center all have specific drop points for a Sprinter versus an SUV, we use the loading-level entrances where the lobby curb is restricted.",
  },
  {
    q: "What about late-night and red-eye arrivals into Buckhead?",
    a: "Our team runs 24/7 and our overnight chauffeurs work the 11pm–5am arrival bank at ATL. A 1:40am landing gets the same staged vehicle and named chauffeur as a midday flight, with an after-hours arrival note to your hotel.",
  },
];

export const Route = createFileRoute("/chauffeur-service-buckhead-from-airport")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "chauffeur service Buckhead from airport, Buckhead airport car service, ATL to Buckhead chauffeur, PDK to Buckhead car service, Buckhead executive transportation, Buckhead luxury airport transfer",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Buckhead Chauffeur From Airport",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Buckhead Airport Chauffeur Service",
            areaServed: [
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Atlanta" },
              { type: "Airport", name: "Hartsfield-Jackson Atlanta International Airport (ATL)" },
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
              { type: "Airport", name: "Fulton County Airport (FTY)" },
            ],
          })
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Buckhead • Executive Airport Transfers"
      h1="Chauffeur Service to Buckhead From Any Atlanta Airport"
      intro="Coming home or checking into Buckhead? My chauffeurs meet you at ATL, PDK, FTY, RYY or LZU, with a Suburban SUV for your smaller party or a Mercedes-Benz Sprinter Limousine for your group."
      heroImage={heroImage}
      heroAlt="Atlanta city skyline from the air, Buckhead chauffeur service from every major airport"
      midImage={midImage}
      midAlt="Atlanta downtown and Buckhead skyline at dusk, luxury airport chauffeur destination"
      sectionOneTitle="From Your Flight to Your Buckhead Door"
      sectionOneBody={[
        "Buckhead is where many of our airport trips begin or end. We serve its hotels, homes and offices from every Atlanta-area airport.",
        "My chauffeurs handle commercial and private arrivals, including transfers to The St. Regis, Four Seasons, Mandarin Oriental and Waldorf Astoria. Tell me your address, flight and luggage so I can help choose your vehicle.",
      ]}
      bulletsTitle="Built for Executive & VIP Buckhead Arrivals"
      bulletsIntro="Your flight, pickup and onward plans coordinated before you land."
      bullets={[
        "Coverage from ATL, PDK, FTY, RYY, and LZU",
        "Live flight tracking with automatic adjustments",
        "Meet & greet at terminal or planeside at FBO",
        "Suburban SUVs for up to 6 or 7; Sprinters for larger parties",
        "Multi-day and as-directed chauffeur programs",
        "Direct EA, concierge, and flight department coordination",
      ]}
      coverageTitle="Buckhead Destinations We Serve Daily"
      coverageIntro="Hotels, residences, offices, and event venues across Buckhead."
      coverage={[
        "The St. Regis Atlanta",
        "Four Seasons Atlanta",
        "Mandarin Oriental Buckhead",
        "Waldorf Astoria Atlanta Buckhead",
        "The Whitley",
        "Phipps Plaza",
        "Lenox Square",
        "Tower Place / 3344 Peachtree",
        "Buckhead residential estates",
      ]}
      routeTable={{
        title: "Airport-to-Buckhead Drive Times",
        intro:
          "Real chauffeured drive times into Buckhead from every Atlanta-area airport, with the routing our chauffeurs actually use.",
        rows: [
          { route: "ATL (Hartsfield-Jackson) → Buckhead", distance: "~17 mi", time: "30–45 min", notes: "Connector to GA-400; 50–65 min in the 4–7pm peak, I-285/Roswell Rd as the relief route." },
          { route: "PDK (DeKalb-Peachtree) → Buckhead", distance: "~11 mi", time: "20–30 min", notes: "Clairmont to I-85 S or Lenox Rd; no downtown exposure." },
          { route: "FTY (Fulton County / Brown Field) → Buckhead", distance: "~15 mi", time: "25–35 min", notes: "I-20 E to the Connector N; west-side alternative to PDK." },
          { route: "RYY (Cobb County / McCollum) → Buckhead", distance: "~26 mi", time: "35–50 min", notes: "I-75 S to I-285 E; avoid Cobb Pkwy at rush hour." },
          { route: "LZU (Gwinnett / Briscoe Field) → Buckhead", distance: "~32 mi", time: "40–60 min", notes: "GA-316 to I-85 S; the most peak-sensitive of the five." },
          { route: "Buckhead → ATL departures", distance: "~17 mi", time: "35–55 min", notes: "Leave 2h45m before a domestic flight, 3h45m international." },
        ],
        footnote:
          "Every transfer is timed to your live flight, so a delay or early landing shifts the pickup automatically.",
      }}
      detailSections={[
        {
          title: "Buckhead Hotel Arrivals: How We Run Them",
          body: [
            "Buckhead's luxury hotels each have their own arrival choreography. We pre-clear yours so the transfer ends at the front desk, not in a driveway queue.",
          ],
          list: [
            "The St. Regis Atlanta, porte-cochère on East Paces Ferry; we notify the arrival desk with your ETA.",
            "Four Seasons Atlanta, 14th St entrance; Sprinter drops at the main door, bell team meets the vehicle.",
            "Mandarin Oriental / The Whitley, covered arrival off Peachtree Rd; short staging window, so we time to the minute.",
            "Waldorf Astoria Buckhead, valet lane arrival; chauffeur handles bags to the bell stand.",
            "Private residences and towers, gate codes and guest-list names cleared before the vehicle heads out.",
          ],
        },
        {
          title: "Meeting You at the Airport",
          list: [
            "ATL domestic: chauffeur texts a numbered baggage-claim door on North or South curb once you land.",
            "ATL international: Concourse F arrivals curb after customs, with 45–75 minutes built into the plan.",
            "PDK, FTY, RYY, LZU: planeside or FBO lobby pickup, staged before your wheels touch down.",
            "Meet-and-greet: name card inside baggage claim with luggage assistance, available on request.",
            "Flight tracking on every booking, delays never cost you the vehicle or a surge fee.",
          ],
        },
        {
          title: "Choosing a Vehicle for a Buckhead Transfer",
          list: [
            "Suburban SUV, executives and couples, 4–5 checked bags, easiest access at tower loading levels.",
            "Executive Sprinter, 8–11 passengers with work tables; the default for board and deal-team arrivals.",
            "Sprinter Limousine, 12–14 passengers with lounge seating for wedding parties and celebration weekends staying in Buckhead.",
            "Multi-vehicle staging, for groups over 14, vehicles arrive in sequence so the whole party reaches the hotel together.",
          ],
        },
      ]}
      gallery={{
        title: "Vehicles Working Buckhead Arrivals",
        intro: "The configurations we stage for Buckhead hotel, residence, and corporate transfers.",
        images: [
          { src: galleryExec, alt: "Executive Sprinter interior used for Buckhead airport chauffeur transfers", caption: "Executive Sprinter, corporate arrivals" },
          { src: galleryLimo, alt: "Mercedes-Benz Sprinter Limousine for Buckhead group airport transfers", caption: "Sprinter Limousine, group and wedding arrivals" },
          { src: gallerySuv, alt: "Black Suburban SUV for Buckhead executive airport transfers", caption: "Suburban SUV, solo and executive transfers" },
        ],
      }}
      faqTitle="Buckhead Airport Chauffeur FAQs"
      relatedTitle="Related Atlanta Airport Services"
      faqs={faqs}
      related={[
        {
          to: "/private-jet-car-service-atlanta",
          title: "Private Jet Car Service Atlanta",
          desc: "Our flagship chauffeur service for private aviation.",
        },
        {
          to: "/car-service-pdk-airport",
          title: "Car Service PDK Airport",
          desc: "Direct PDK-to-Buckhead executive transfers.",
        },
        {
          to: "/atlanta-private-airport-transfer",
          title: "Atlanta Private Airport Transfer",
          desc: "Premium transfers from every Atlanta private airport.",
        },
      ]}
      ctaTitle="Text Me Your Buckhead Address"
      ctaBody="Send me your flight, destination, passenger count and bags. I'll check your transfer myself and send your quote in writing."
    />
  );
}
